import React, { useState, useEffect, useCallback, memo } from "react";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import MenuBar from "@/components/MenuBar";
import ApiService, { ApiError } from "@/services/api";
import { EndpointKeys } from "@/config/api";
import {
  DEFAULT_EMPLOYER_ADDRESS,
  EmployerData,
  EmployerResponse,
} from "@/types/employer";
import { ParentInformation } from "@/types/parent_information";
import type { PayrunsResponse } from "@/types/payrun";
import { toast } from "@/components/ui/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useAddressValidation } from "@/hooks/useAddressValidation";
import { Building, Menu, AlertTriangle, Info } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import SuperUserEmployerSelector from "@/components/common/SuperUserEmployerSelector";
import { isSuperUser } from "@/types/user";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { MultiAgentSmartBot } from "@/components/customer-ai/MultiAgentSmartBot";
import { MultiAgentSmartBotWidget } from "@/components/customer-ai/MultiAgentSmartBotWidget";
import { useChatWidgetStore } from "@/stores/useChatWidgetStore";
import { useFeatureEnabled } from "@/contexts/FeatureFlagContext";

// Define a separate EmployerForm component to avoid re-renders
interface EmployerFormProps {
  initialData: EmployerData;
  onSubmit: (data: EmployerData) => Promise<void>;
  disabled: boolean;
  hasApprovedPayruns: boolean;
  isSuperUser: boolean;
}

const EmployerForm = memo(
  ({
    initialData,
    onSubmit,
    disabled,
    hasApprovedPayruns,
    isSuperUser,
  }: EmployerFormProps) => {
    const [formData, setFormData] = useState<EmployerData>(initialData);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    
    // Use address validation hook for country-specific validation
    const addressValidation = useAddressValidation();

    // Reset form data when initialData changes
    useEffect(() => {
      setFormData(initialData);
    }, [initialData]);

    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        if (name.startsWith("address.")) {
          const addressField = name.split(".")[1];
          setFormData((prev) => ({
            ...prev,
            address: {
              ...prev.address,
              [addressField]: value || undefined,
            },
          }));
        } else {
          setFormData((prev) => ({ ...prev, [name]: value || undefined }));
        }

        validateField(name, value);
      },
      [],
    );

    const validateField = useCallback((name: string, value: string) => {
      let error = "";
      if (!value) {
        setErrors((prev) => ({ ...prev, [name]: "" }));
        return;
      }

      switch (name) {
        case "payeReferenceNumber":
          if (!/^\d{3}\/[a-zA-Z0-9]{1,10}$/.test(value)) {
            error =
              "Invalid format. Should be 3 numbers, a forward slash, and 1-10 alphanumeric characters.";
          }
          break;
        case "accountsOfficeNumber":
          if (!/^\d{3}P[A-Z](\d{8}|\d{7}X)$/.test(value)) {
            error =
              "Invalid format. Should be 3 numbers, 'P', a letter, and 8 numbers (or 7 numbers and 'X').";
          }
          break;
        case "address.postal_code":
          const postalResult = addressValidation.validatePostalCode(value);
          if (!postalResult.isValid) {
            error = postalResult.error;
          }
          break;
        case "address.street_number":
          const streetResult = addressValidation.validateStreetNumber(value);
          if (!streetResult.isValid) {
            error = streetResult.error;
          }
          break;
      }
      setErrors((prev) => ({ ...prev, [name]: error }));
    }, []);

    const handleSubmit = useCallback(
      async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (Object.values(errors).some((error) => error !== "")) {
          alert("Please correct the errors before submitting.");
          return;
        }

        const submitData = {
          ...formData,
          address: {
            ...formData.address,
            country_code: "GB",
          },
        };

        await onSubmit(submitData);
      },
      [formData, errors, onSubmit],
    );

    const renderInput = useCallback(
      (
        name: string,
        label: string,
        value: string = "",
        type: string = "text",
        placeholder?: string,
      ) => (
        <div key={name}>
          <Label htmlFor={name}>{label}</Label>
          <Input
            id={name}
            name={name}
            value={value || ""}
            onChange={handleInputChange}
            type={type}
            placeholder={placeholder}
            disabled={disabled || (hasApprovedPayruns && !isSuperUser)}
          />
          {errors[name] && (
            <p className="text-red-500 text-sm mt-1">{errors[name]}</p>
          )}
        </div>
      ),
      [handleInputChange, errors, disabled, hasApprovedPayruns, isSuperUser],
    );

    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {renderInput(
            "payeReferenceNumber",
            "PAYE Reference Number",
            formData.payeReferenceNumber,
          )}
          {renderInput(
            "accountsOfficeNumber",
            "Accounts Office Number",
            formData.accountsOfficeNumber,
          )}
          {renderInput(
            "gatewayCredentialsNumber",
            "Govt Gateway User ID",
            formData.gatewayCredentialsNumber,
          )}
          {renderInput(
            "gatewayCredentialsPassword",
            "Govt Gateway Password",
            formData.gatewayCredentialsPassword,
            "password",
            "Leave empty to preserve existing password",
          )}
          {renderInput(
            "payeGatewayAuthorizationCode",
            "PAYE Gateway Authorisation Code",
            formData.payeGatewayAuthorizationCode,
          )}
          {renderInput(
            "address.street_number",
            "Street Number",
            formData.address?.street_number,
          )}
          {renderInput("address.route", "Street Name", formData.address?.route)}
          {renderInput(
            "address.locality",
            "Town/City",
            formData.address?.locality,
          )}
          {renderInput("address.state", "County", formData.address?.state)}
          {renderInput(
            "address.postal_code",
            "Postcode",
            formData.address?.postal_code,
          )}
          {renderInput("address.country", "Country", formData.address?.country)}
        </div>
        <Button
          type="submit"
          disabled={
            Object.values(errors).some((error) => error !== "") || (disabled && !isSuperUser)
          }
          className="w-full"
        >
          Save Employer
        </Button>
      </form>
    );
  },
);

EmployerForm.displayName = "EmployerForm";

const EmployerManagement: React.FC = () => {
  const { user } = useAuth();
  const { openChat } = useChatWidgetStore();
  const isMultiAgentBotEnabled = useFeatureEnabled("multi_agent_bot");
  const [hasApprovedPayruns, setHasApprovedPayruns] = useState<boolean>(false);
  const [canEdit, setCanEdit] = useState<boolean>(true);
  const [employer, setEmployer] = useState<EmployerData | null>(null);
  const [editingEmployer, setEditingEmployer] = useState<EmployerData>({
    address: DEFAULT_EMPLOYER_ADDRESS,
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Superuser specific state
  const isSuperUserAccount = isSuperUser(user);
  const [selectedUserId, setSelectedUserId] = useState<string>("current");
  const [hasSelectedEmployer, setHasSelectedEmployer] = useState(false);

  useEffect(() => {
    if (isSuperUserAccount && selectedUserId !== "current") {
      setHasSelectedEmployer(true);
    }
  }, [selectedUserId, isSuperUserAccount]);

  const handleUserChange = (userId: string) => {
    setSelectedUserId(userId);
  };

  // Only fetch data if we have a selectedUser (or we're not a superuser)
  useEffect(() => {
    if (!isSuperUserAccount || hasSelectedEmployer) {
      const initializeData = async () => {
        setIsLoading(true);
        try {
          await Promise.all([
            fetchEmployer(),
            fetchParentInfo(),
            fetchPayruns(),
          ]);
        } finally {
          setIsLoading(false);
        }
      };
      initializeData();
    }
  }, [selectedUserId, isSuperUserAccount, hasSelectedEmployer]);

  const fetchEmployer = async () => {
    try {
      const queryParams: Record<string, string | number> = {};
      if (selectedUserId !== "current") {
        queryParams.userId = selectedUserId;
      }

      const response = await ApiService.request<EmployerData>(
        EndpointKeys.FETCH_EMPLOYER,
        {
          queryParams,
        },
      );
      setEmployer(response);
      setEditingEmployer({
        ...response,
        gatewayCredentialsPassword: undefined,
      });
    } catch (error) {
      console.error("Error fetching employer:", error);
    }
  };

  const fetchParentInfo = async () => {
    try {
      const queryParams: Record<string, string | number> = {};
      if (selectedUserId !== "current") {
        queryParams.userId = selectedUserId;
      }

      const response = await ApiService.request<{
        parent_info: ParentInformation;
      }>(EndpointKeys.GET_PARENT_INFO, {
        queryParams,
      });

      const isEditable =
        response.parent_info.is_switching_provider ||
        response.parent_info.is_registered_employer;

      // Let superuser always edit, unless there are approved payruns
      setCanEdit(isSuperUserAccount || isEditable);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        return;
      }
      console.error("An unexpected error occurred");
    }
  };

  const fetchPayruns = async () => {
    try {
      const queryParams: Record<string, string | number> = {};
      if (selectedUserId !== "current") {
        queryParams.userId = selectedUserId;
      }

      const data = await ApiService.request<PayrunsResponse>(
        EndpointKeys.GET_PAYRUNS,
        {
          queryParams,
        },
      );

      const checkForApprovedOrPaidPayruns = data.payruns.some(
        (payrun) => payrun.status === "Approved" || payrun.status === "Paid",
      );
      setHasApprovedPayruns(checkForApprovedOrPaidPayruns);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch payruns. Please try again.",
      });
      console.error("Error fetching payruns:", error);
    }
  };

  const handleEditClick = useCallback(() => {
    if (employer) {
      setEditingEmployer({
        ...employer,
        gatewayCredentialsPassword: undefined,
      });
    }
    setIsDialogOpen(true);
  }, [employer]);

  const handleDialogChange = useCallback((open: boolean) => {
    setIsDialogOpen(open);
  }, []);

  const handleFormSubmit = useCallback(
    async (submitData: EmployerData) => {
      try {
        const queryParams: Record<string, string | number> = {};
        if (selectedUserId !== "current") {
          queryParams.userId = selectedUserId;
        }

        // Prepare the data to submit, excluding empty password to preserve existing one
        const dataToSubmit = { ...submitData };
        if (!dataToSubmit.gatewayCredentialsPassword) {
          delete dataToSubmit.gatewayCredentialsPassword;
        }

        await ApiService.request<EmployerResponse>(
          EndpointKeys.CREATE_EMPLOYER,
          {
            method: "POST",
            body: dataToSubmit,
            queryParams,
          },
        );

        await fetchEmployer();
        setIsDialogOpen(false);

        toast({
          title: "Success",
          description: "Employer information has been updated successfully.",
        });
      } catch (error) {
        console.error("Error saving employer:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to save employer information. Please try again.",
        });
      }
    },
    [selectedUserId, fetchEmployer],
  );

  // Loading state
  if (isLoading && (isSuperUserAccount ? hasSelectedEmployer : true)) {
    return (
      <div className="flex min-h-screen bg-background">
        {/* Sidebar - always visible in loading state */}
        <div className="hidden md:block fixed md:relative w-64 h-full bg-gray-900 z-20">
          <MenuBar />
        </div>

        <div className="flex-1 w-full md:w-auto flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  // Edit Employer Button component - defined as a regular function, not with useCallback
  const EditEmployerButton = () => (
    <Dialog open={isDialogOpen} onOpenChange={handleDialogChange}>
      <DialogTrigger asChild>
        <Button onClick={handleEditClick} disabled={!canEdit}>
          {employer ? "Edit Employer" : "Create Employer"}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
        <DialogHeader>
          <DialogTitle>
            {employer ? "Edit Employer" : "Create Employer"}
          </DialogTitle>
          <DialogDescription>
            {employer && hasApprovedPayruns
              ? "This data should only be changed after a payrun has been completed by Hirelo accountants as this can affect HMRC compliance"
              : "All the employment information will be filled out by Hirelo given that we will be interfacing with the HMRC on your behalf. If you haven't registered with the HMRC, you can leave all the fields empty and save the form with the default values. Given that we will handle all HMRC correspondence, we provide our corporate HQ address."}
          </DialogDescription>
        </DialogHeader>
        <EmployerForm
          initialData={editingEmployer}
          onSubmit={handleFormSubmit}
          disabled={!canEdit}
          hasApprovedPayruns={hasApprovedPayruns}
          isSuperUser={isSuperUserAccount}
        />
      </DialogContent>
    </Dialog>
  );

  const MainContent = () => {
    // If superuser without selected employer, show selection prompt
    if (isSuperUserAccount && !hasSelectedEmployer) {
      return (
        <CardContent>
          <div className="flex flex-col items-center justify-center p-12 text-center">
            <div className="mb-4 rounded-full bg-yellow-100 p-4">
              <AlertTriangle className="h-8 w-8 text-yellow-500" />
            </div>
            <h2 className="mb-2 text-xl font-semibold">
              Please Select an Employer
            </h2>
            <p className="max-w-md text-gray-600">
              As a superuser, you need to select an employer from the dropdown
              above before viewing or managing employer data.
            </p>
          </div>
        </CardContent>
      );
    }

    // Regular content for users or superusers with selected employer
    return (
      <CardContent>
        <ScrollArea className="w-full">
          {employer ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">
                          PAYE Reference
                        </p>
                        <h3 className="text-lg font-semibold">
                          {employer.payeReferenceNumber || "-"}
                        </h3>
                      </div>
                      <Building className="h-8 w-8 text-primary" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">
                          Accounts Office Number
                        </p>
                        <h3 className="text-lg font-semibold">
                          {employer.accountsOfficeNumber || "-"}
                        </h3>
                      </div>
                      <Building className="h-8 w-8 text-primary" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">
                          Gateway Number
                        </p>
                        <h3 className="text-lg font-semibold">
                          {employer.gatewayCredentialsNumber || "-"}
                        </h3>
                      </div>
                      <Building className="h-8 w-8 text-primary" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <h3 className="text-lg font-semibold">Address Details</h3>
                </CardHeader>
                <CardContent>
                  {employer.address ? (
                    <div className="space-y-2">
                      <p>
                        {employer.address.street_number}{" "}
                        {employer.address.route}
                      </p>
                      <p>{employer.address.locality}</p>
                      <p>
                        {employer.address.state &&
                          `${employer.address.state}, `}
                        {employer.address.postal_code}
                      </p>
                      <p>{employer.address.country}</p>
                    </div>
                  ) : (
                    <p>No address information available</p>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-8">
                <Building className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground text-center">
                  No employer data available. Please create an employer.
                </p>
              </CardContent>
            </Card>
          )}
        </ScrollArea>
      </CardContent>
    );
  };

  const openChatWithQuestion = (question: string) => {
    openChat(question);
  };

  const QuickActions = () => (
    <div className="flex gap-2 flex-wrap">
      <Button
        variant="outline"
        size="sm"
        onClick={() => openChatWithQuestion("What's my PAYE reference number?")}
      >
        <Info className="h-4 w-4 mr-2" />
        View PAYE Details
      </Button>

      <Button
        variant="outline"
        size="sm"
        onClick={() =>
          openChatWithQuestion("I need help updating my employer information")
        }
      >
        <Building className="h-4 w-4 mr-2" />
        Update Information
      </Button>

      <Button
        variant="outline"
        size="sm"
        onClick={() =>
          openChatWithQuestion(
            "Explain what employer information I need to provide"
          )
        }
      >
        <Info className="h-4 w-4 mr-2" />
        What Do I Need?
      </Button>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-background">
      {/* Mobile Menu Toggle */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 flex items-center p-4 bg-gray-900">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="text-white"
        >
          <Menu className="h-6 w-6" />
        </Button>

        {/* Add a centered title for mobile */}
        <div className="flex-1 text-center">
          <h2 className="text-white text-sm font-semibold">
            Employer Management
          </h2>
        </div>
      </div>

      {/* Sidebar - expanded based on mobile menu state */}
      <div
        className={`${
          isMobileMenuOpen ? "block" : "hidden"
        } md:block fixed md:relative w-64 h-full bg-gray-900 z-20 overflow-auto`}
      >
        <MenuBar />
      </div>

      {/* Main Content */}
      <div className="flex-1 w-full md:w-auto mt-16 md:mt-0">
        <div className="p-4 md:p-6 max-w-7xl space-y-6">
          {/* User Selector - ONLY for mobile, appears at top of content when menu closed */}
          {isSuperUserAccount && (
            <div className="md:hidden mb-4">
              <SuperUserEmployerSelector
                selectedUserId={selectedUserId}
                onUserChange={handleUserChange}
              />
            </div>
          )}

          <Card>
            <CardHeader className="flex flex-col space-y-4">
              {/* Title row with button */}
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold">Employer Management</h2>

                {/* Edit button - rendered conditionally */}
                {(!isSuperUserAccount ||
                  (isSuperUserAccount && hasSelectedEmployer)) && (
                  <EditEmployerButton />
                )}
              </div>

              {/* Help text and superuser info in separate row */}
              <div>
                {isSuperUserAccount && (
                  <p className="text-sm text-muted-foreground">
                    {selectedUserId !== "current"
                      ? "Viewing and managing employer data for selected user"
                      : "Viewing your own employer data"}
                  </p>
                )}
                <Alert className="mt-2">
                  <Info className="h-4 w-4 text-primary" />
                  <AlertTitle>Employer Registration</AlertTitle>
                  <AlertDescription>
                    All the employment information will generally be filled out
                    by Hirelo as we will be interfacing with the HMRC on your
                    behalf. If you haven't registered with the HMRC, you can
                    leave the employer details blank. If you have already
                    registered as an employer, please provide the details, and
                    we'll continue managing HMRC correspondence under that
                    entity for you.
                  </AlertDescription>
                </Alert>
                {isMultiAgentBotEnabled && <QuickActions />}
              </div>

              {/* SuperUser selector - rendered conditionally */}
              {isSuperUserAccount && (
                <div className="hidden md:block">
                  <SuperUserEmployerSelector
                    selectedUserId={selectedUserId}
                    onUserChange={handleUserChange}
                  />
                </div>
              )}
            </CardHeader>

            <MainContent />
          </Card>

          <MultiAgentSmartBotWidget />
        </div>
      </div>
    </div>
  );
};

export default EmployerManagement;
